import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { 
  Trophy, 
  Calendar, 
  Clock, 
  MapPin, 
  ChevronLeft,
  Ticket,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Live Badge
const LiveBadge = () => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-red-500 text-white rounded-full animate-pulse">
    <span className="w-1.5 h-1.5 bg-white rounded-full"></span>
    LIVE
  </span>
);

// Fixture Card Component
const FixtureCard = ({ fixture, onClick }) => (
  <motion.div
    whileHover={{ scale: 1.02, y: -4 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className="group relative bg-white/[0.03] backdrop-blur-sm border border-white/10 hover:border-[#0055FF]/50 rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl hover:shadow-[#0055FF]/10"
  >
    <div className="absolute inset-0 bg-gradient-to-r from-[#0055FF]/0 via-[#0055FF]/5 to-purple-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    
    <div className="relative p-5">
      {/* Live Status */}
      <div className="flex items-center justify-end mb-4">
        {fixture.is_live && <LiveBadge />}
      </div>
      
      {/* Teams */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex-1 text-center">
          <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-white/10 flex items-center justify-center">
            {fixture.home_logo ? (
              <img src={fixture.home_logo} alt={fixture.home_team} className="w-8 h-8 object-contain" />
            ) : (
              <span className="text-xl">⚽</span>
            )}
          </div>
          <p className="font-semibold text-white text-sm">{fixture.home_team}</p>
        </div>
        
        <div className="px-4">
          <span className="text-2xl font-bold text-gray-500">VS</span>
        </div>
        
        <div className="flex-1 text-center">
          <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-white/10 flex items-center justify-center">
            {fixture.away_logo ? (
              <img src={fixture.away_logo} alt={fixture.away_team} className="w-8 h-8 object-contain" />
            ) : (
              <span className="text-xl">⚽</span>
            )}
          </div>
          <p className="font-semibold text-white text-sm">{fixture.away_team}</p>
        </div>
      </div>
      
      {/* Date, Time, Venue */}
      <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-400 mb-4">
        <div className="flex items-center gap-1.5">
          <Calendar className="w-4 h-4 text-[#0055FF]" />
          <span>{fixture.date}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Clock className="w-4 h-4 text-[#0055FF]" />
          <span>{fixture.time}</span>
        </div>
      </div>
      
      <div className="flex items-center justify-center gap-1.5 text-sm text-gray-500 mb-4">
        <MapPin className="w-4 h-4" />
        <span>{fixture.venue}</span>
      </div>
      
      {/* Price & CTA */}
      <div className="flex items-center justify-between pt-4 border-t border-white/10">
        <div>
          <p className="text-xs text-gray-500">From</p>
          <p className="text-xl font-bold text-[#0055FF]">D{fixture.ticket_price}</p>
        </div>
        <Button className="bg-gradient-to-r from-[#0055FF] to-purple-600 hover:from-[#0066FF] hover:to-purple-500 text-white">
          <Ticket className="w-4 h-4 mr-2" />
          Get Tickets
        </Button>
      </div>
    </div>
  </motion.div>
);

export default function TournamentPage() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTournament = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/tournaments/${tournamentId}`);
        setTournament(response.data);
      } catch (error) {
        console.error('Failed to fetch tournament:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTournament();
  }, [tournamentId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0F0F]">
        <Navbar />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="w-8 h-8 text-[#0055FF] animate-spin" />
        </div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="min-h-screen bg-[#0F0F0F]">
        <Navbar />
        <div className="text-center py-40">
          <Trophy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Tournament Not Found</h3>
          <Link to="/football">
            <Button variant="outline" className="mt-4 border-white/20 text-white hover:bg-white/10">
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to Football
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const fixtures = tournament.fixtures || [];

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0055FF]/10 via-transparent to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link to="/football" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors">
            <ChevronLeft className="w-5 h-5" />
            <span>Back to Football</span>
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-white/10 flex items-center justify-center text-5xl">
              {tournament.flag || '🏆'}
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              {tournament.name}
            </h1>
            
            <p className="text-xl text-gray-400 mb-2">
              {tournament.country}
            </p>
            
            {tournament.description && (
              <p className="text-gray-500 max-w-2xl mx-auto">
                {tournament.description}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Fixtures */}
      <section className="py-16 bg-[#0F0F0F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white">Upcoming Fixtures</h2>
              <p className="text-gray-400 mt-1">{fixtures.length} matches available</p>
            </div>
          </div>

          {fixtures.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {fixtures.map((fixture, index) => (
                <motion.div
                  key={fixture.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <FixtureCard
                    fixture={fixture}
                    onClick={() => navigate(`/fixture/${fixture.id}`)}
                  />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Trophy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Fixtures Available</h3>
              <p className="text-gray-400">Check back later for upcoming matches.</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#0F0F0F] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-500">© 2026 Gam Ticket. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
